import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const changeLanguage = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
  }, [i18n]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo with highlight */}
          <Link to="/" className="flex items-center gap-3 shrink-0 group">
            <div className="relative rounded-lg p-1.5 ring-2 ring-primary-400 transition-all duration-300 group-hover:ring-accent-400">
              <img
                src="https://storage.helloreaddy.io/project_files/92e1f7d8-4b49-4026-a33b-e8c282efe1cd/e044e290-bfd7-421d-a771-db88820eaff2_compressed__client-DeiaSpart.webp"
                alt="DEIASPART"
                className="h-9 md:h-11 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Nav with highlight */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative font-medium text-sm transition-all duration-200 whitespace-nowrap px-3 py-2 rounded-md ${
                    isActive
                      ? scrolled || !isHome
                        ? 'bg-primary-50 text-primary-600'
                        : 'bg-background-50/15 text-background-50'
                      : scrolled || !isHome
                        ? 'text-foreground-700 hover:bg-background-100 hover:text-primary-500'
                        : 'text-background-100 hover:bg-background-50/10 hover:text-background-50'
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className={`absolute bottom-0 left-3 right-3 h-0.5 rounded-full ${
                      scrolled || !isHome ? 'bg-primary-400' : 'bg-accent-400'
                    }`} />
                  )}
                </Link>
              );
            })}

            {/* Divider */}
            <span className={`w-px h-5 mx-2 ${
              scrolled || !isHome ? 'bg-background-300' : 'bg-background-50/30'
            }`} />

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-all duration-300 whitespace-nowrap px-3 py-2 rounded-full ring-2 ${
                  scrolled || !isHome
                    ? 'bg-primary-50 text-primary-600 ring-primary-300 hover:bg-primary-100'
                    : 'bg-background-50/15 text-background-50 ring-background-50/40 backdrop-blur-sm hover:bg-background-50/25'
                }`}
              >
                <i className="ri-global-line w-4 h-4 flex items-center justify-center"></i>
                <span>{i18n.language === 'pt' ? 'PT' : 'EN'}</span>
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-background-50 border border-background-200 rounded-lg overflow-hidden min-w-[100px] shadow-lg">
                  <button
                    onClick={() => changeLanguage('pt')}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-background-100 transition-colors ${
                      i18n.language === 'pt' ? 'text-primary-500 font-medium' : 'text-foreground-700'
                    }`}
                  >
                    Português
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-background-100 transition-colors ${
                      i18n.language === 'en' ? 'text-primary-500 font-medium' : 'text-foreground-700'
                    }`}
                  >
                    English
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              scrolled || !isHome
                ? 'text-foreground-700 hover:bg-background-100'
                : 'text-background-50 hover:bg-background-50/10'
            }`}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl w-6 h-6 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-primary-50 text-primary-500'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-background-200 mt-2 pt-2">
              <p className="px-3 text-xs text-foreground-500 mb-1">{t('nav.lang')}</p>
              <div className="flex gap-2 px-3">
                <button
                  onClick={() => changeLanguage('pt')}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    i18n.language === 'pt'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  Português
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    i18n.language === 'en'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  English
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}